import type { CreditNote, Invoice, Order } from "@estelle/domain";

/** A row flattened to the field names its module declares. */
export type Projection = Record<string, unknown>;

/**
 * An invoice flattened for display.
 *
 * Unlike `projectOrder` in the Orders module, nothing here is a join. An
 * `Invoice` is deliberately a snapshot — seller and buyer details, the tax
 * split, every total — copied onto it at generation time rather than read
 * live from the order or the company profile (see the comment on `Invoice`
 * itself), so the row already carries everything this list shows.
 */
export function projectInvoice(invoice: Invoice): Projection {
  return {
    invoiceNumber: invoice.invoiceNumber ?? "",
    invoiceDate: invoice.invoiceDate ?? "",
    status: invoice.status,
    buyerName: invoice.buyerName,
    placeOfSupplyStateCode: invoice.placeOfSupplyStateCode,
    total: invoice.total,
    taxKind: invoice.taxKind,
    buyerGstin: invoice.buyerGstin ?? "",
  };
}

/** A credit note flattened for display. Same reasoning as `projectInvoice`. */
export function projectCreditNote(creditNote: CreditNote): Projection {
  return {
    creditNoteNumber: creditNote.creditNoteNumber ?? "",
    creditNoteDate: creditNote.creditNoteDate ?? "",
    status: creditNote.status,
    reason: creditNote.reason,
    total: creditNote.total,
    taxKind: creditNote.taxKind,
    placeOfSupplyStateCode: creditNote.placeOfSupplyStateCode,
  };
}

/**
 * An order flattened for the Uninvoiced Orders table (FR-GST-18).
 *
 * A small, local field set rather than importing the Orders module's own —
 * that module owns its column declarations for its own filter builder, and
 * reaching into another module's `fields.ts`/`projections.ts` is the kind of
 * cross-module edge the module split exists to avoid. This table shows just
 * enough to identify the order and decide whether to chase it.
 */
export function projectUninvoicedOrder(order: Order): Projection {
  return {
    orderNumber: order.orderNumber,
    placedAt: order.placedAt,
    customerName: order.shippingAddress?.name ?? "",
    shippingProvinceCode: order.shippingAddress?.provinceCode ?? "",
    total: order.total,
  };
}
